/**
 * 打印模板：分行页眉 / 页脚 / 落款（线格本式）
 * - template_config.headerLines / footerLines / closingLines 为行数组
 * - 至少一行有可见文字时优先于整段 header / footer 富文本
 */

import { PRINT_ZONE_LINE_CLASS } from '@/utils/printZoneLineContent'

export { PRINT_ZONE_LINE_CLASS }

function lineHasVisibleContent (line) {
  const raw = String(line?.content ?? '')
  const t = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  return t.length > 0
}

export function hasHeaderLines (templateConfig) {
  const arr = templateConfig?.headerLines
  return Array.isArray(arr) && arr.length > 0 && arr.some(lineHasVisibleContent)
}

export function hasFooterLines (templateConfig) {
  const arr = templateConfig?.footerLines
  return Array.isArray(arr) && arr.length > 0 && arr.some(lineHasVisibleContent)
}

export function hasClosingLines (templateConfig) {
  const arr = templateConfig?.closingLines
  return Array.isArray(arr) && arr.length > 0 && arr.some(lineHasVisibleContent)
}

/** 与预览/导出一致 */
export function shouldRenderHeaderLines (templateConfig) {
  return hasHeaderLines(templateConfig)
}

export function shouldRenderFooterLines (templateConfig) {
  return hasFooterLines(templateConfig)
}

export function shouldRenderClosingLines (templateConfig) {
  return hasClosingLines(templateConfig)
}

export function applyHeaderFooterPagePlaceholders (html, pageNo = 1, totalPages = 1) {
  if (html == null || html === '') return ''
  const pn = String(Math.max(1, Number(pageNo) || 1))
  const tp = String(Math.max(1, Number(totalPages) || 1))
  let s = String(html)
    .replace(/\uFEFF/g, '')
    .replace(/\u200B/g, '')
    .replace(
      /(<span[^>]*class="[^"]*(?:page-number|total-pages)[^"]*"[^>]*style="[^"]*)display\s*:\s*block\s*;?/gi,
      '$1display:inline-block;'
    )

  /** 富文本 token：替换 span 内任意占位符（含零宽字符、嵌套标签） */
  s = s.replace(
    /(<span[^>]*class="[^"]*page-number[^"]*"[^>]*>)([\s\S]*?)(<\/span>)/gi,
    `$1${pn}$3`
  )
  s = s.replace(
    /(<span[^>]*class="[^"]*total-pages[^"]*"[^>]*>)([\s\S]*?)(<\/span>)/gi,
    `$1${tp}$3`
  )

  return s
    .replace(/\{page_number}/gi, pn)
    .replace(/\{total_pages}/gi, tp)
}

/**
 * @param {object} line - { content, fontSizePx, fontFamily, fontWeight, textAlign, minHeightPx }
 * @param {object} page - template_config.page
 * @param {'header'|'footer'|'closing'} zone
 */
export function printZoneLineBoxStyle (line, page, zone = 'header') {
  const p = page || {}
  const defaultFs = defaultZoneRootFontSizePx(p, zone)
  const fsRaw = Number(line?.fontSizePx)
  const fs =
    Number.isFinite(fsRaw) && fsRaw > 0 ? Math.min(72, Math.max(7, Math.round(fsRaw))) : defaultFs
  const lineLhOverride = Number(line?.lineHeight)
  const pageLhRaw = Number(zoneDefaultLineHeight(p, zone))
  const pageLh = Number.isFinite(pageLhRaw) && pageLhRaw > 0 ? pageLhRaw : 1.22
  const lineHeight =
    Number.isFinite(lineLhOverride) && lineLhOverride > 0 ? lineLhOverride : pageLh
  const minFromPage =
    zone === 'footer'
      ? Math.max(0, Number(p.footerLineMinHeightPx ?? 0) || 0)
      : zone === 'closing'
        ? Math.max(0, Number(p.closingLineMinHeightPx ?? 0) || 0)
        : Math.max(0, Number(p.headerLineMinHeightPx ?? 0) || 0)
  const minFromLine = Math.max(0, Number(line?.minHeightPx ?? 0) || 0)
  const minH = Math.max(minFromPage, minFromLine)
  const fixedH = Number(line?.heightPx)
  const align = line?.textAlign === 'left' || line?.textAlign === 'right' ? line.textAlign : 'center'
  const fw = line?.fontWeight === 'bold' || line?.fontWeight === 700 || line?.fontWeight === '700' ? '700' : '400'
  const st = {
    fontSize: `${fs}px`,
    lineHeight: String(lineHeight),
    textAlign: align,
    fontWeight: fw,
    boxSizing: 'border-box',
    width: '100%',
    whiteSpace: 'pre-wrap'
  }
  const ff = String(line?.fontFamily || '').trim()
  if (ff) st.fontFamily = ff
  const lsRaw = Number(line?.letterSpacingPx)
  if (line?.letterSpacingPx != null && line?.letterSpacingPx !== '' && Number.isFinite(lsRaw)) {
    st.letterSpacing = `${Math.max(-2, Math.min(48, lsRaw))}px`
  }
  if (Number.isFinite(fixedH) && fixedH > 0) {
    st.height = `${Math.min(240, Math.max(8, Math.round(fixedH)))}px`
    st.overflow = 'hidden'
    st.display = 'flex'
    st.alignItems = 'center'
    if (align === 'center') st.justifyContent = 'center'
    else if (align === 'right') st.justifyContent = 'flex-end'
    else st.justifyContent = 'flex-start'
  } else if (minH > 0) {
    st.minHeight = `${minH}px`
  }
  const border =
    zone === 'footer'
      ? p.footerLineBorderBottom === true
      : zone === 'closing'
        ? p.closingLineBorderBottom === true
        : p.headerLineBorderBottom === true
  if (border) {
    st.borderBottom = '1px solid rgba(0, 0, 0, 0.14)'
    st.paddingBottom = '2px'
  }
  return st
}

/** 分行页眉/页脚/落款：解析单行实际生效的字号、行距等 */
export function resolveZoneLineEffectiveMetrics (line, page, zone = 'header') {
  const p = page || {}
  const defaultFs = defaultZoneRootFontSizePx(p, zone)
  const fsRaw = Number(line?.fontSizePx)
  const fontSizePx =
    Number.isFinite(fsRaw) && fsRaw > 0 ? Math.min(72, Math.max(7, Math.round(fsRaw))) : defaultFs
  const lineLhOverride = Number(line?.lineHeight)
  const pageLhRaw = Number(zoneDefaultLineHeight(p, zone))
  const pageLh = Number.isFinite(pageLhRaw) && pageLhRaw > 0 ? pageLhRaw : 1.22
  const lineHeight =
    Number.isFinite(lineLhOverride) && lineLhOverride > 0 ? lineLhOverride : pageLh
  const globalMin =
    zone === 'footer'
      ? Math.max(0, Number(p.footerLineMinHeightPx ?? 0) || 0)
      : zone === 'closing'
        ? Math.max(0, Number(p.closingLineMinHeightPx ?? 0) || 0)
        : Math.max(0, Number(p.headerLineMinHeightPx ?? 0) || 0)
  const lineMinRaw = Number(line?.minHeightPx)
  const lineMin =
    Number.isFinite(lineMinRaw) && lineMinRaw > 0 ? Math.max(0, Math.round(lineMinRaw)) : 0
  const minHeightPx = Math.max(globalMin, lineMin)
  const heightRaw = Number(line?.heightPx)
  const heightPx =
    Number.isFinite(heightRaw) && heightRaw > 0 ? Math.min(240, Math.max(8, Math.round(heightRaw))) : null
  return {
    fontSizePx,
    fontSizeFromPage: defaultFs,
    fontSizeCustom: Number.isFinite(fsRaw) && fsRaw > 0,
    lineHeight,
    lineHeightFromPage: pageLh,
    lineHeightCustom: Number.isFinite(lineLhOverride) && lineLhOverride > 0,
    heightPx,
    heightCustom: heightPx != null,
    minHeightPx,
    minHeightFromPage: globalMin,
    minHeightCustom: Number.isFinite(lineMinRaw) && lineMinRaw > 0,
    letterSpacingPx: (() => {
      if (line?.letterSpacingPx === '' || line?.letterSpacingPx == null) return null
      const n = Number(line?.letterSpacingPx)
      return Number.isFinite(n) ? n : null
    })(),
    letterSpacingCustom: (() => {
      if (line?.letterSpacingPx === '' || line?.letterSpacingPx == null) return false
      return Number.isFinite(Number(line?.letterSpacingPx))
    })()
  }
}

export function defaultPrintHeaderLine () {
  return {
    content: '',
    fontSizePx: null,
    fontFamily: '',
    fontWeight: 'normal',
    textAlign: 'center',
    minHeightPx: null,
    /** 固定行高 px；与 minHeightPx 二选一优先固定高度 */
    heightPx: null,
    /** 行高倍数；空则用页眉/页脚 Tab 的全局行高 */
    lineHeight: null,
    /** 字符间距 px；「等宽拉伸」自动写入，也可手动微调 */
    letterSpacingPx: null
  }
}

export function defaultPrintFooterLine () {
  return defaultPrintHeaderLine()
}

export function defaultPrintClosingLine () {
  return defaultPrintHeaderLine()
}

function defaultZoneRootFontSizePx (page, zone) {
  const p = page || {}
  if (zone === 'closing') {
    const closing = Number(p.closingRootFontSizePx)
    if (Number.isFinite(closing) && closing > 0) {
      return Math.min(22, Math.max(7, Math.round(closing)))
    }
  }
  return Math.min(22, Math.max(7, Number(p.headerRootFontSizePx) || 11))
}

function zoneDefaultLineHeight (page, zone) {
  const p = page || {}
  if (zone === 'footer') {
    return p.footerContentLineHeight ?? p.headerContentLineHeight ?? 1.22
  }
  if (zone === 'closing') {
    return p.closingContentLineHeight ?? p.signaturesLineHeight ?? p.headerContentLineHeight ?? 1.22
  }
  return p.headerContentLineHeight ?? 1.22
}

export function normalizeClosingLines (arr) {
  return normalizeHeaderLines(arr)
}

export function normalizeHeaderLines (arr) {
  if (!Array.isArray(arr)) return []
  return arr.map((raw) => {
    const r = raw && typeof raw === 'object' ? raw : {}
    const d = defaultPrintHeaderLine()
    return {
      ...d,
      content: String(r.content ?? ''),
      fontSizePx: r.fontSizePx === '' || r.fontSizePx == null ? null : Number(r.fontSizePx),
      fontFamily: String(r.fontFamily ?? '').trim(),
      fontWeight: r.fontWeight === 'bold' || r.fontWeight === 700 ? 'bold' : 'normal',
      textAlign: ['left', 'center', 'right'].includes(r.textAlign) ? r.textAlign : 'center',
      minHeightPx: r.minHeightPx === '' || r.minHeightPx == null ? null : Number(r.minHeightPx),
      heightPx: (() => {
        if (r.heightPx === '' || r.heightPx == null) return null
        const n = Number(r.heightPx)
        return Number.isFinite(n) && n > 0 ? n : null
      })(),
      lineHeight: (() => {
        if (r.lineHeight === '' || r.lineHeight == null) return null
        const n = Number(r.lineHeight)
        return Number.isFinite(n) && n > 0 ? n : null
      })(),
      letterSpacingPx: (() => {
        if (r.letterSpacingPx === '' || r.letterSpacingPx == null) return null
        const n = Number(r.letterSpacingPx)
        return Number.isFinite(n) ? Math.max(-2, Math.min(48, n)) : null
      })()
    }
  })
}

/** 供「送货单号」探测等：拼接各行纯文本 */
export function headerLinesPlainProbe (templateConfig, replaceVars, richToPlain) {
  if (!hasHeaderLines(templateConfig)) return ''
  let t = ''
  for (const line of templateConfig.headerLines || []) {
    const raw = applyHeaderFooterPagePlaceholders(replaceVars(String(line?.content || '')), 1, 1)
    t += ` ${richToPlain ? richToPlain(raw) : raw}`
  }
  return t
}

export function footerLinesPlainProbe (templateConfig, replaceVars, richToPlain) {
  if (!hasFooterLines(templateConfig)) return ''
  let t = ''
  for (const line of templateConfig.footerLines || []) {
    const raw = applyHeaderFooterPagePlaceholders(replaceVars(String(line?.content || '')), 1, 1)
    t += ` ${richToPlain ? richToPlain(raw) : raw}`
  }
  return t
}

export function closingLinesPlainProbe (templateConfig, replaceVars, richToPlain) {
  if (!hasClosingLines(templateConfig)) return ''
  let t = ''
  for (const line of templateConfig.closingLines || []) {
    const raw = applyHeaderFooterPagePlaceholders(replaceVars(String(line?.content || '')), 1, 1)
    t += ` ${richToPlain ? richToPlain(raw) : raw}`
  }
  return t
}
